import { Tag } from 'baby-design'
import css from './ProductDescription.styles.scss'
import { ProductNotes } from '../ProductNotes/ProductNotes'
import { StoreIcons } from 'baby-design'
import classNames from 'classnames'
import { Product } from '../../RegItemCard.types'
import { formatDisplayPriceForWholeDollars } from '../../../../utils/productHelpers'

interface ProductDescriptionProps {
  size: 'desktop' | 'tablet' | 'phone'
  product: Product
}

export const ProductDescription = ({
  size,
  product,
}: ProductDescriptionProps) => {
  if (size === 'desktop') {
    return <ProductDescriptionDesktop product={product} />
  } else if (size === 'tablet') {
    return <ProductDescriptionTablet product={product} />
  } else {
    return <ProductDescriptionMobile product={product} />
  }
}

const Headings = ({
  heading,
  subheading,
  url,
}: {
  heading: string
  subheading?: string
  url: string
}) => (
  <>
    <a className={css.product_description__heading} href={url} target="_blank">
      {heading}
    </a>
    {subheading && (
      <div className={css.product_description__subheading}>{subheading}</div>
    )}
  </>
)

const Tags = ({
  tags,
  showVariant = false,
}: {
  tags: Array<string>
  showVariant?: boolean
}) => {
  const specialTags = new Set(['Must-have'])

  return (
    <div className={css.product_description__tags}>
      {tags.map((tag) => (
        <Tag
          size="sm"
          type="primary"
          variant={showVariant && specialTags.has(tag) ? 'special' : 'normal'}
          key={tag}
        >
          {tag}
        </Tag>
      ))}
    </div>
  )
}

const ProductDescriptionDesktop = ({ product }: { product: Product }) => {
  return (
    <div
      className={classNames(
        css.product_description,
        css.product_description__desktop
      )}
    >
      <Headings
        heading={product.heading}
        subheading={product.subheading}
        url={product.url}
      />
      {product.tags && <Tags tags={product.tags} showVariant={true} />}
      {product.offers && product.offers.length > 0 && product.price && (
        <div className={css.product_description__price}>
          {product.offers && product.offers.length > 1 && 'From '}
          {formatDisplayPriceForWholeDollars(product.price)}
        </div>
      )}
      {product.notes && (
        <div className={css.product_description__notes}>
          <ProductNotes notes={product.notes} />
          <div className={css.product_description__notes_beak} />
        </div>
      )}
    </div>
  )
}

const ProductDescriptionTablet = ({ product }: { product: Product }) => {
  return (
    <div
      className={classNames(
        css.product_description,
        css.product_description__tablet
      )}
    >
      <Headings
        heading={product.heading}
        subheading={product.subheading}
        url={product.url}
      />
      {product.offers && product.price && (
        <StoreIcons
          stores={product.offers.map((offer) => ({
            name: offer.storeDisplayName,
          }))}
          price={formatDisplayPriceForWholeDollars(product.price)}
          size="medium"
        />
      )}
      {product.tags && <Tags tags={product.tags} />}
    </div>
  )
}

const ProductDescriptionMobile = ({ product }: { product: Product }) => {
  return (
    <div
      className={classNames(
        css.product_description,
        css.product_description__mobile
      )}
    >
      <Headings
        heading={product.heading}
        subheading={product.subheading}
        url={product.url}
      />
      {product.offers && product.price && (
        <StoreIcons
          stores={product.offers.map((offer) => ({
            name: offer.storeIconName,
          }))}
          price={formatDisplayPriceForWholeDollars(product.price)}
          size="small"
        />
      )}
      {product.tags && <Tags tags={product.tags} />}
    </div>
  )
}
